import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, Injector, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { JSO_NATA_SCHEMA_VARIANT_PRODUCT_COSTS_CALCULATION } from '@phoenix/common';
import { ButtonModule } from 'primeng/button';
import { lastValueFrom } from 'rxjs';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../../@core/services/modal/modal.service';
import { NotificationService } from '../../../../@core/services/notification/notification.service';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import {
    CalculatoryCostsExpressionEngine,
    CalculatoryCostsSourceMode,
    Product,
    ProductType,
    RecalculateProductCalculatoryCostsResult,
} from '../../../../generated/generated-types';
import {
    CALCULATORY_COSTS_JSONATA_EXPRESSION_TEMPLATES,
    getJsonataEditorDialogOptions,
    JsonataEditorDialogComponent,
} from '../../../../shared/components/jsonata-editor/jsonata-editor-dialog.component';
import { PhxInputNumberComponent } from '../../../../shared/components/phx-input-number/phx-input-number.component';
import { PhxSelect } from '../../../../shared/components/phx-select/phx-select.component';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import { FormlyJsoNataSchemasDropDownComponent } from '../../../custom-formly/formly-jso-nata-schemas-drop-down/formly-jso-nata-schemas-drop-down.component';
import { ProductService, RecalculateProductCalculatoryCostsConfigOverride } from '../../services/product.service';
import { applyProductCalculationUpdatesToModel } from '../../utils/apply-product-calculation-updates';
import { buildCalculatoryCostsJsonataContext } from '../../utils/build-calculatory-costs-jsonata-context';

type CalculatoryCostsSourceModeOption = CalculatoryCostsSourceMode | null;
type CalculatoryCostsExpressionBindingMode = 'schema' | 'inline';

interface SelectOption<T> {
    label: string;
    value: T;
}

@Component({
    selector: 'app-formly-product-calculatory-costs',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TranslatePipe,
        ButtonModule,
        PhxSelect,
        LocaleDatePipe,
        FormlyJsoNataSchemasDropDownComponent,
        PhxInputNumberComponent,
    ],
    templateUrl: './formly-product-calculatory-costs.component.html',
})
export class FormlyProductCalculatoryCostsComponent extends FieldType<FieldTypeConfig> implements OnInit {
    private readonly modalService = inject(ModalService);
    private readonly i18n = inject(I18nService);
    private readonly productService = inject(ProductService);
    private readonly notificationService = inject(NotificationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly injector = inject(Injector);

    readonly CalculatoryCostsSourceMode = CalculatoryCostsSourceMode;
    readonly CalculatoryCostsExpressionEngine = CalculatoryCostsExpressionEngine;
    readonly CALCULATORY_COSTS_JSONATA_EXPRESSION_TEMPLATES = CALCULATORY_COSTS_JSONATA_EXPRESSION_TEMPLATES;
    readonly productCostsSchemaVariant = JSO_NATA_SCHEMA_VARIANT_PRODUCT_COSTS_CALCULATION;
    readonly calculatoryCostsJsonataEditorSource = computed(() => buildCalculatoryCostsJsonataContext(this.productModel));
    readonly recalculating = signal(false);

    readonly sourceModeItems = signal<SelectOption<CalculatoryCostsSourceModeOption>[]>([]);
    readonly expressionBindingModeItems = signal<SelectOption<CalculatoryCostsExpressionBindingMode>[]>([
        { label: '', value: 'schema' },
        { label: '', value: 'inline' },
    ]);
    readonly engineItems = signal<SelectOption<CalculatoryCostsExpressionEngine>[]>([
        { label: 'JSONATA', value: CalculatoryCostsExpressionEngine.JSONATA },
    ]);

    /** Tracked locally so OnPush + computed react to mode changes (model is not a signal). */
    readonly sourceMode = signal<CalculatoryCostsSourceModeOption>(null);
    readonly expressionEngine = signal<CalculatoryCostsExpressionEngine | null>(null);
    readonly expressionBindingMode = signal<CalculatoryCostsExpressionBindingMode>('inline');

    readonly usesTenantDefault = computed(() => this.sourceMode() == null);

    readonly isManual = computed(() => this.sourceMode() === CalculatoryCostsSourceMode.MANUAL);

    readonly isExpression = computed(() => this.sourceMode() === CalculatoryCostsSourceMode.EXPRESSION);

    readonly isSchemaExpression = computed(() => this.isExpression() && this.expressionBindingMode() === 'schema');

    readonly isInlineExpression = computed(() => this.isExpression() && this.expressionBindingMode() === 'inline');

    readonly isPlugin = computed(() => this.sourceMode() === CalculatoryCostsSourceMode.PLUGIN);

    readonly showRecalculate = computed(() => !this.isManual());

    readonly priceDisabled = computed(() => !this.isManual());

    constructor() {
        super();
    }

    /** Prefer detailModel — formly field.model can lag behind async product load (OnPush + smartRender tabs). */
    get productModel(): Product {
        const detail = this.options?.formState?.detailComponent as BaseDetailComponent<Product> | undefined;
        return detail?.detailModel ?? this.model;
    }

    ngOnInit(): void {
        this.rebuildSourceModeItems();
        this.rebuildExpressionBindingModeItems();
        this.syncSignalsFromModel();
        this.ensureValidSourceMode();

        const detailComponent = this.options?.formState?.detailComponent as BaseDetailComponent<Product> | undefined;
        if (detailComponent) {
            toObservable(detailComponent.detailModelSig, { injector: this.injector })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.syncSignalsFromModel();
                    this.cdRef.markForCheck();
                });
        }

        const detailForm = detailComponent?.detailForm;
        detailForm?.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.rebuildSourceModeItems();
                if (this.resetTradingSetRollupModeIfInvalid(true)) {
                    return;
                }
                this.cdRef.markForCheck();
            });
    }

    onSourceModeChange(value: CalculatoryCostsSourceModeOption): void {
        const model = this.productModel;
        model.calculatoryCostsSourceMode = value;
        this.sourceMode.set(value);
        if (value === CalculatoryCostsSourceMode.EXPRESSION && !model.calculatoryCostsExpressionEngine) {
            model.calculatoryCostsExpressionEngine = CalculatoryCostsExpressionEngine.JSONATA;
            this.expressionEngine.set(CalculatoryCostsExpressionEngine.JSONATA);
        }
        this.notifyModelChanged();
    }

    onEngineChange(value: CalculatoryCostsExpressionEngine): void {
        this.productModel.calculatoryCostsExpressionEngine = value;
        this.expressionEngine.set(value);
        this.notifyModelChanged();
    }

    onExpressionBindingModeChange(value: CalculatoryCostsExpressionBindingMode): void {
        const model = this.productModel;
        this.expressionBindingMode.set(value);
        if (value === 'schema') {
            model.calculatoryCostsExpression = null;
        } else {
            model.calculatoryCostsExpressionSchemaId = null;
        }
        this.notifyModelChanged();
    }

    onExpressionSchemaIdChange(schemaId: string | null | undefined): void {
        this.productModel.calculatoryCostsExpressionSchemaId = schemaId ?? null;
        this.notifyModelChanged();
    }

    onCalculatoryCostsChange(value: number | null): void {
        this.productModel.calculatoryCosts = value;
        this.notifyModelChanged();
    }

    openExpressionDialog(): void {
        const model = this.productModel;
        this.modalService.fromComponent(JsonataEditorDialogComponent, getJsonataEditorDialogOptions({
            expression: model.calculatoryCostsExpression ?? '',
            source: buildCalculatoryCostsJsonataContext(model),
            sourceReadOnly: true,
            expressionTemplates: CALCULATORY_COSTS_JSONATA_EXPRESSION_TEMPLATES,
            hintKey: 'product.calculatory-costs-expression-dialog-hint',
            dataTestIdPrefix: 'product-calculatory-costs-expression',
        }, this.i18n.translate(_('product.calculatory-costs-expression-dialog-title')))).pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                if (typeof result === 'string') {
                    this.productModel.calculatoryCostsExpression = result;
                    this.notifyModelChanged();
                }
            });
    }

    async recalculateNow(): Promise<void> {
        const productId = this.productModel?.id;
        if (!productId) {
            return;
        }
        this.recalculating.set(true);
        try {
            const result = await lastValueFrom(
                this.productService.recalculateProductCalculatoryCosts(productId, true, this.buildRecalculateConfigOverride()),
            );
            if (result.error) {
                this.notificationService.error(result.error);
            } else {
                this.applyRecalculationResult(result);
                this.notifyModelChanged();
                this.notificationService.success(this.i18n.translate(_('product.calculatory-costs-recalculated')));
            }
        } catch (error: any) {
            this.notificationService.error(error?.message ?? this.i18n.translate(_('common.error-occurred')));
        } finally {
            this.recalculating.set(false);
        }
    }

    onStrategyCodeChange(): void {
        this.notifyModelChanged();
    }

    private syncSignalsFromModel(): void {
        const model = this.productModel;
        this.sourceMode.set(model?.calculatoryCostsSourceMode ?? null);
        this.expressionEngine.set(model?.calculatoryCostsExpressionEngine ?? null);
        this.expressionBindingMode.set(this.resolveExpressionBindingModeFromModel(model));
        this.rebuildSourceModeItems();
        this.rebuildExpressionBindingModeItems();
    }

    private resolveExpressionBindingModeFromModel(model = this.productModel): CalculatoryCostsExpressionBindingMode {
        if (model?.calculatoryCostsExpression?.trim()) {
            return 'inline';
        }
        if (model?.calculatoryCostsExpressionSchemaId) {
            return 'schema';
        }
        return 'inline';
    }

    private rebuildExpressionBindingModeItems(): void {
        this.expressionBindingModeItems.set([
            {
                label: this.i18n.translate(_('product.calculatory-costs-expression-binding-schema')),
                value: 'schema',
            },
            {
                label: this.i18n.translate(_('product.calculatory-costs-expression-binding-inline')),
                value: 'inline',
            },
        ]);
    }

    private isTradingSetProduct(): boolean {
        return this.productModel?.productType === ProductType.TRADINGSET;
    }

    private rebuildSourceModeItems(): void {
        const items: SelectOption<CalculatoryCostsSourceModeOption>[] = [
            {
                label: this.i18n.translate(_('product.calculatory-costs-mode-tenant-default')),
                value: null,
            },
            { label: this.i18n.translate(_('product.calculatory-costs-mode-manual')), value: CalculatoryCostsSourceMode.MANUAL },
            { label: this.i18n.translate(_('product.calculatory-costs-mode-expression')), value: CalculatoryCostsSourceMode.EXPRESSION },
        ];
        if (this.isTradingSetProduct()) {
            items.push({
                label: this.i18n.translate(_('product.calculatory-costs-mode-trading-set-rollup')),
                value: CalculatoryCostsSourceMode.TRADING_SET_ROLLUP,
            });
        }
        //for now dont show
        // items.push({
        //     label: this.i18n.translate(_('product.calculatory-costs-mode-plugin')),
        //     value: CalculatoryCostsSourceMode.PLUGIN,
        // });
        this.sourceModeItems.set(items);
    }

    private ensureValidSourceMode(): void {
        this.resetTradingSetRollupModeIfInvalid(false);
    }

    /** @returns true when mode was reset and callers should skip further UI work */
    private resetTradingSetRollupModeIfInvalid(markDirty: boolean): boolean {
        if (this.isTradingSetProduct() || this.sourceMode() !== CalculatoryCostsSourceMode.TRADING_SET_ROLLUP) {
            return false;
        }
        this.productModel.calculatoryCostsSourceMode = CalculatoryCostsSourceMode.MANUAL;
        this.sourceMode.set(CalculatoryCostsSourceMode.MANUAL);
        if (markDirty) {
            this.notifyModelChanged();
        }
        return true;
    }

    private buildRecalculateConfigOverride(): RecalculateProductCalculatoryCostsConfigOverride {
        const model = this.productModel;
        if (model.calculatoryCostsSourceMode == null) {
            return {
                sourceMode: CalculatoryCostsSourceMode.EXPRESSION,
                expressionEngine: model.calculatoryCostsExpressionEngine ?? this.expressionEngine() ?? undefined,
                expression: '',
                expressionSchemaId: model.calculatoryCostsExpressionSchemaId ?? null,
                strategyCode: null,
            };
        }

        return {
            sourceMode: model.calculatoryCostsSourceMode,
            expressionEngine: model.calculatoryCostsExpressionEngine ?? this.expressionEngine() ?? undefined,
            expression: model.calculatoryCostsExpression ?? '',
            expressionSchemaId: model.calculatoryCostsExpressionSchemaId ?? null,
            strategyCode: model.calculatoryCostsStrategyCode ?? null,
        };
    }

    private notifyModelChanged(): void {
        this.form?.markAsDirty();
        (this.options?.formState?.detailComponent as BaseDetailComponent<Product> | undefined)?.notifyDirtyRecheck();
        this.cdRef.markForCheck();
    }

    private applyRecalculationResult(result: RecalculateProductCalculatoryCostsResult): void {
        const model = this.productModel;
        if (result.updatedFields) {
            applyProductCalculationUpdatesToModel(
                model,
                result.updatedFields as Record<string, unknown>,
            );
        } else if (result.calculatoryCosts != null) {
            model.calculatoryCosts = result.calculatoryCosts;
        }

        if (result.calculatedAt) {
            model.calculatoryCostsCalculatedAt = result.calculatedAt;
        }

        this.syncSignalsFromModel();
    }
}
